<template>
  <v-dialog v-model="showForm" max-width="780px" @click:outside="onCancelClick">
    <template v-slot:activator="{ on, attrs }">
      <!--addXLButton-->
      <v-btn v-if="buttonType === 'addXLButton'" color="primary" v-bind="attrs" v-on="on" rounded class="elevation-0 monospace-font white--text"><v-icon>add</v-icon>{{ $t("scoutingAnalysis.addReport") }}</v-btn>
      <!--addFullWidthButton-->
      <v-btn v-if="buttonType === 'addFullWidthButton'" color="primary" v-bind="attrs" v-on="on" rounded block class="elevation-0 monospace-font white--text"><v-icon>add</v-icon>{{ $t("scoutingAnalysis.addReport") }}</v-btn>
      <!--editButton-->
      <v-btn v-if="buttonType === 'editButton'" elevation="0" icon x-small class="ms-1" color="grey lighten-1" v-bind="attrs" v-on="on"><v-icon small>edit</v-icon></v-btn>
      <!--addXSButton-->
      <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="buttonType === 'addXSButton'">
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="secondary" class="mr-1 px-1 elevation-0" style="min-width:30px;" 
            v-bind="attrs"
            @click.stop="onDialogOpen"  
            v-on="on"  
            small>
            <v-icon style="font-size: 19px;">flare</v-icon>
          </v-btn>
        </template>
        <span class="black--text body-1">{{ $t('scoutingAnalysis.addReport') }}</span>
      </v-tooltip>
      <!--itemListButton-->
      <v-list-item @click="onDialogOpen" v-if="buttonType === 'itemListButton'">
        <v-list-item-title>
          <v-icon style="font-size: 19px;">flare</v-icon>
          <span class="pl-2">{{ $t('scoutingAnalysis.addReport') }}</span>
        </v-list-item-title>
      </v-list-item>
      <!--addFullWidthSlimButton-->
      <v-btn v-if="buttonType === 'addFullWidthSlimButton'" v-bind="attrs" v-on="on" class="elevation-0 monospace-font white--text mb-2" small rounded outlined block color="primary">
        <v-icon>add</v-icon>
        {{ $t("scoutingAnalysis.addPlayerReport") }}
      </v-btn>
    </template>
    <v-card>
      <v-card-title class="secondary white--text d-flex align-center justify-space-between">
        <span class="header-font" style="font-size: 22px;">
          <v-icon style="font-size: 18px;" color="white" class="mr-2">add</v-icon>
          {{ $t("scoutingAnalysis.addReport") }}
        </span>
      </v-card-title>
      <v-card-text>
        <v-form name="form" ref="formRef" @submit.prevent="onSaveClick">
          <!--Player-->
          <v-row class="py-3" v-if="showAddPlayerForm" align="end">
            <v-col cols="12" md="4" class="py-0">
              <SearchBar
                class="mt-n1"
                placeholder="general.search"
                icon="person_add"
                :dataSet="dataSet"
                :navigate="false"
                :playersResults="10"
                :showPlayers="true"
                :showTeams="false"
                :showGames="false"
                :showLeagues="false"
                :showMore="false"
                @resultClicked="onResultClicked($event)"
              ></SearchBar>  
            </v-col>
            <v-col cols="12" md="4" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('playerDetail.player') }}</p>
              <v-autocomplete dense hide-details outlined readonly clearable append-icon="" class="slim-input"
                v-model="analysis.playerId"
                :items="searchbarSelectedPlayers"
                item-value="id"
                item-text="text"
                menu-props="auto"
                @click:clear="handleClearSelectedPlayer()"
                :rules="requiredRules"
              >
                <template v-slot:selection="{item}">
                  <v-card class="d-flex align-center" elevation="0" color="transparent">
                    <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{ item.text }}</v-card-text>
                  </v-card>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="4" class="py-0">
              <AddPlayerModal :disabledBtn="!!analysis.playerId" :dataSet="dataSet" @dialogClosed="handleAddPlayerClosed($event)" :key="addPlayerModalKey"></AddPlayerModal>
            </v-col>
          </v-row>
          <v-divider class="mt-2" v-if="showAddPlayerForm"></v-divider>
          <v-row class="py-3" align="center">
            <!--Recommendation-->
            <v-col cols="12" md="6" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('documents.recommendation.label') }}</p>
              <v-autocomplete dense outlined clearable item-value="id" item-text="text" class="slim-input"
                v-model="analysis.recommendationId"
                :items="recommendationItems"
                :hint="$t('scoutingCalendar.tasks.accommodation.required')" :persistent-hint="!analysis.recommendationId"
                :rules="requiredRules"
              >
                <template v-slot:selection="{item}"><v-card-text class="pa-0 primary--text font-weight-medium body-1">{{ item.text }}</v-card-text></template>
                <template v-slot:item="{item}"><span>{{ item.text }}</span></template>  
              </v-autocomplete>
            </v-col>
            <!--Visibility-->
            <v-col cols="12" md="6" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('collections.visibility') }}</p>
              <v-autocomplete dense clearable outlined class="slim-input"
                v-model="analysis.visibilityId" 
                :items="visibilities"
                item-value="id" item-text="name" 
                :rules="requiredRules"
              >
                <template v-slot:selection="{item}">
                  <v-card-text class="pa-0 primary--text font-weight-medium body-1"><v-icon class="me-1" x-small>{{ item.icon }}</v-icon>{{ item.name }}</v-card-text>
                </template>
                <template v-slot:item="{item, attrs, on }">
                  <v-tooltip right color="lightSecondary lighten-4" max-width="300">
                    <template v-slot:activator="{ on: tooltipOn }">
                      <v-card-text class="pa-0 primary--text font-weight-medium body-1" v-bind="attrs" v-on="{ ...on, ...tooltipOn }">
                        <v-icon class="me-1" x-small>{{ item.icon }}</v-icon>{{ item.name }}
                      </v-card-text>
                    </template>
                    <span class="black--text body-1">{{ $t(`collections.privacity.${item.description}`) }}</span>
                  </v-tooltip>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row align="start">
            <!--Global rating-->
            <v-col cols="12" md="3" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.globalRating') }}</p>
              <v-autocomplete dense outlined clearable class="slim-input"
                v-model="analysis.finalRating"
                :items="rankingItems"
                :hint="$t('scoutingCalendar.tasks.accommodation.required')" :persistent-hint="!analysis.finalRating"
                :rules="requiredRules"
              >
                <template v-slot:selection="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
                <template v-slot:item="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="9" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.conclusion') }}</p>
              <v-textarea counter outlined height="90" :placeholder="$t('documents.notesPlaceholderAlt')"
              :rules="rules" 
              v-model="analysis.finalNotes"></v-textarea>
            </v-col>
            <!--Technical-->
            <v-col cols="12" md="3" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.technical') }}</p>
              <v-autocomplete dense outlined clearable class="slim-input"
                v-model="analysis.technicalRating"
                :items="rankingItems"
              >
                <template v-slot:selection="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
                <template v-slot:item="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="9" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.technicalNotes') }}</p>
              <v-textarea counter outlined height="90" :placeholder="$t('documents.notesPlaceholderAlt')"
              :rules="rules" 
              v-model="analysis.technicalNotes"></v-textarea>
            </v-col>
            <!--Tactical-->
            <v-col cols="12" md="3" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.tactical') }}</p>
              <v-autocomplete dense outlined clearable class="slim-input"
                v-model="analysis.tacticalRating"
                :items="rankingItems"
              >
                <template v-slot:selection="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
                <template v-slot:item="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
              </v-autocomplete>             
            </v-col>
            <v-col cols="12" md="9" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.tacticalNotes') }}</p>
              <v-textarea counter outlined height="90" :placeholder="$t('documents.notesPlaceholderAlt')"
              :rules="rules" 
              v-model="analysis.tacticalNotes"></v-textarea>
            </v-col>
            <!--Physical-->
            <v-col cols="12" md="3" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.physical') }}</p>
              <v-autocomplete dense outlined clearable class="slim-input"
                v-model="analysis.physicalRating"
                :items="rankingItems"
              >
                <template v-slot:selection="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
                <template v-slot:item="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="9" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.physicalNotes') }}</p>
              <v-textarea counter outlined height="90" :placeholder="$t('documents.notesPlaceholderAlt')"
                :rules="rules" 
                v-model="analysis.physicalNotes"></v-textarea>
            </v-col>
            <!--Mental-->
            <v-col cols="12" md="3" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.mental') }}</p>
              <v-autocomplete dense outlined clearable class="slim-input"
                v-model="analysis.mentalRating"
                :items="rankingItems"
              >
                <template v-slot:selection="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
                <template v-slot:item="{item}"><v-chip small class="monospace-font" :color="getRankingColor(item)">{{ item }}</v-chip></template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="9" class="py-0">
              <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingAnalysis.mentalNotes') }}</p>
              <v-textarea counter outlined height="90" :placeholder="$t('documents.notesPlaceholderAlt')"
                :rules="rules" 
                v-model="analysis.mentalNotes"></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <!--Buttons-->
      <v-card-actions class="pr-6 pb-4 d-flex justify-end">
        <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary'" rounded outlined class="elevation-0 monospace-font px-4" @click="onCancelClick" :disabled="isSaving">{{$t('general.cancel')}}</v-btn>
        <v-btn rounded class="elevation-0 monospace-font white--text px-4" color="primary"
          @click="onSaveClick" 
          :loading="isSaving" 
          
        >{{$t('general.save')}}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import * as _ from 'lodash';
import { colorScale , getPositionCode, getChipColorByPosition, userHasRole} from '../../../utils/functions';
import scoutingAnalysisService from '../../../services/scoutingAnalysis.service';
import SearchBar from '../../base/SearchBar.vue';
import AddPlayerModal from '../../player/AddPlayerModal.vue';
import Dictionary from '../../../dictionary';

export default {
  name: 'AddPlayerScoutingAnalysis',
  props: {
    buttonType: {
      type: String,
      default: 'addXLButton' //Types: addXLButton, addXSButton, editButton, itemListButton, addFullWidthButton
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    analysisToUpdate: {
      type: Object,
      default: () => {
        return {};
      }
    },
    showAddPlayerForm: {
      type: Boolean,
      default: false
    },
    task: {
      type: Object,
      default: () => {
        return {};
      }
    },
  },
  components: { 
    SearchBar, AddPlayerModal
  },
  data() {
    return {
      showForm: false,
      isSaving: false,
      analysis: {  visibilityId: 2, technicalNotes: '', tacticalNotes: '', physicalNotes: '', mentalNotes: '', finalNotes: '' },
      visibilities: [{ id: 2, name: this.$t('collections.privacity.privateTitle'), icon: 'lock', description: 'privateDescription' }, 
        { id: 3, name: this.$t('collections.privacity.supervisedTitle'), icon: 'visibility', description: 'supervisedDescription' }, 
        { id: 1, name: this.$t('collections.privacity.publicTitle'), icon: 'public', description: 'publicDescription' }],
      rankingItems: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      rules: [(v) => v.length <= 1000 || this.$t('scoutingAnalysis.charactersMax')],
      recommendationItems: [{ id: 1, text: this.$t('documents.recommendation.1') }, 
        { id: 2, text: this.$t('documents.recommendation.2') }, 
        { id: 3, text: this.$t('documents.recommendation.3') }],
      dataSet: null,
      searchbarSelectedPlayers: [],
      addPlayerModalKey: 0,
      requiredRules: [v => v != null || Dictionary[this.$i18n.locale]["general"]["formErrors"]["required"]]
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    darkMode(){
      return this.currentUser.darkMode || this.$vuetify.theme.dark;
    },
    isFromTask(){
      const taskKeys = Object.keys(this.task);
      return taskKeys.length > 0;
    }
  },
  created() {
    //If player sheet, set playerId automatically
    if (this.$route.params.id) this.analysis.playerId = +this.$route.params.id;

    //Editing
    if (this.isEditing) {
      this.analysis = _.cloneDeep(this.analysisToUpdate); //Keep original content in a different object
    };

    //For Granada client, add another recommnedation option
    if (this.currentUser.clientId === 7 || this.userHasRole('ROLE_ADMIN')) {
      this.recommendationItems.push({ id: 4, text: this.$t('documents.recommendation.4') });
    };
  },
  async mounted() {
    if (this.showAddPlayerForm) await this.loadDataSet();

    //isFromTask
    if (this.isFromTask) {
      this.analysis.taskId = this.task.id;
      this.analysis.visibilityId = this.task.visibilityId;
      if (this.task.scoutedPlayers.length) this.handleAddPlayerClosed(this.task.scoutedPlayers[0]);
    };
  },
  methods: {
    onCancelClick() {
      //Reset analysis values
      this.analysis = { 
        visibilityId: 2,
        technicalRating: null,
        technicalNotes: '',
        tacticalRating: null,
        tacticalNotes: '',
        physicalRating: null,
        physicalNotes: '',
        mentalRating: null,
        mentalNotes: '',
        recommendationId: null,
        finalNotes: '',
        finalRating: null
      };
      this.$emit("dialogClosed");
      this.isSaving = false;
      this.showForm = !this.showForm;
    },
    async onSaveClick() {
      if (this.$refs.formRef.validate()) {
        this.isSaving = true;
        if (this.isEditing)  await scoutingAnalysisService.updatePlayerScoutingAnalysis(this.analysis);
        else await scoutingAnalysisService.createPlayerScoutingAnalysis(this.analysis);
        this.onCancelClick();
      } else {
        this.isSaving = false;
      };
    },
    getRankingColor(value) {
      if (value) return colorScale(1, 10, value, 90);
    },
    onDialogOpen(event) {
      event.stopPropagation();
      this.showForm = true;
    },
    onResultClicked(event) {
      this.searchbarSelectedPlayers.push(event);
      this.analysis.playerId = event.id;
    },
    async loadDataSet() {
      this.dataSet = await this.$store.dispatch("getDataSet");
    },
    getPositionChip(position) {
      return getChipColorByPosition(position);
    },
    getPositionCode(position) {
      return getPositionCode(this.currentUser.fieldPositionCodeId, position);
    },
    onImageLoadFailure(event) {
      event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
    },
    handleAddPlayerClosed(event) {
      if (event.id) {
        this.searchbarSelectedPlayers = [];
        this.analysis.playerId = event.id;
        this.searchbarSelectedPlayers.push({ id: event.id, text: event.name });
      };
      this.addPlayerModalKey++;
    },
    handleClearSelectedPlayer() {
      this.searchbarSelectedPlayers = [];
      this.analysis.playerId = null;
    },
    userHasRole(role){
      return userHasRole(this.currentUser, role);
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/views/scouting-analysis/player-scouting-analysis/AddPlayerScoutingAnalysis.vue